import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaBriefcase, FaShoppingCart, FaUser, FaCalendarAlt, 
  FaGraduationCap, FaRocket, FaRing, FaHandHoldingHeart 
} from 'react-icons/fa';

const Services = () => {
  const [activeService, setActiveService] = useState(null);
  
  const services = [
    {
      id: "portfolio",
      title: "Portfolio Websites",
      icon: <FaBriefcase />,
      description: "Showcase your work with stunning visual presentations and smooth interactions. Perfect for artists, photographers, and creative professionals.",
      features: ["Custom designs", "Project galleries", "Interactive elements", "Contact forms"],
      color: "cyber-pink",
      image: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "business",
      title: "Business Websites",
      icon: <FaBriefcase />,
      description: "Establish your professional online presence with modern, responsive business websites that drive conversions and grow your brand.",
      features: ["Corporate styling", "Service sections", "Team profiles", "Client testimonials"],
      color: "cyber-purple",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "ecommerce",
      title: "E-Commerce Stores",
      icon: <FaShoppingCart />,
      description: "Sell products online with secure, user-friendly e-commerce websites featuring payment integration and inventory management.",
      features: ["Product catalogs", "Secure checkout", "Payment gateways", "Order tracking"],
      color: "cyber-neon",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "personal-brand",
      title: "Personal Brand Sites",
      icon: <FaUser />,
      description: "Build your personal brand with a website that highlights your expertise, achievements, and services to attract opportunities.",
      features: ["Personal branding", "Biography sections", "Achievement showcases", "Content blogs"],
      color: "cyber-blue",
      image: "https://images.unsplash.com/photo-1493119508027-2b584f234d6c?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "booking",
      title: "Booking/Appointment Sites",
      icon: <FaCalendarAlt />,
      description: "Enable clients to book your services online with intuitive scheduling systems and automatic confirmations.",
      features: ["Calendar integration", "Automatic reminders", "Payment processing", "Admin dashboard"],
      color: "cyber-pink",
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "educational",
      title: "Educational Platforms",
      icon: <FaGraduationCap />,
      description: "Create engaging learning experiences with custom educational websites featuring course management and student tracking.",
      features: ["Course modules", "Video lectures", "Quizzes & assignments", "Student progress tracking"],
      color: "cyber-purple",
      image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "landing",
      title: "Landing Pages",
      icon: <FaRocket />,
      description: "Maximize conversions with high-impact landing pages designed to capture leads and promote specific products or services.",
      features: ["Conversion-focused design", "Call-to-action sections", "Lead generation forms", "A/B testing capability"],
      color: "cyber-neon",
      image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "event",
      title: "Event & Wedding Sites",
      icon: <FaRing />,
      description: "Celebrate special occasions with beautiful event websites that provide information and allow guests to RSVP.",
      features: ["Event details", "RSVP system", "Gift registry", "Photo galleries"],
      color: "cyber-blue",
      image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "ngo",
      title: "NGO & Donation Platforms",
      icon: <FaHandHoldingHeart />,
      description: "Support causes with websites that tell your organization's story and facilitate online donations for your mission.",
      features: ["Donation processing", "Campaign pages", "Impact reporting", "Volunteer sign-up"],
      color: "cyber-pink",
      image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=800&q=80"
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-orbitron font-bold mb-6">
            Our <span className="text-cyber-neon">Services</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We create stunning websites tailored to your specific needs. Explore our services below
            to find the perfect solution for your project.
          </p>
        </motion.div>

        {/* Interactive Service Selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`cyber-card overflow-hidden cursor-pointer relative ${activeService === service.id ? 'ring-2 ring-' + service.color : ''}`}
              onClick={() => setActiveService(service.id === activeService ? null : service.id)}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-${service.color}/40 backdrop-blur-sm flex items-center justify-center`}>
                  <span className="text-5xl text-white">{service.icon}</span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className={`text-xl font-orbitron font-bold mb-2 text-${service.color}`}>
                  {service.title}
                </h3>
                <p className="text-gray-300 line-clamp-2">
                  {service.description}
                </p>
              </div>
              
              {/* Expanded content */}
              {activeService === service.id && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="p-6 pt-0"
                >
                  <div className="border-t border-gray-700 my-4"></div>
                  
                  <h4 className="font-orbitron text-white mb-2">Key Features:</h4>
                  <ul className="list-disc pl-5 mb-4 text-gray-300 space-y-1">
                    {service.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                  
                  <Link 
                    to={`/services/${service.id}`}
                    className={`bg-${service.color}/20 text-${service.color} border border-${service.color}/50 hover:bg-${service.color}/30 transition-colors px-4 py-2 rounded font-medium inline-block mt-2`}
                  >
                    Learn More
                  </Link>
                </motion.div>
              )}
              
              {/* Neon corner */}
              <div className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-${service.color}`}></div>
              <div className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-${service.color}`}></div>
            </motion.div>
          ))}
        </div>

        {/* Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-orbitron font-bold mb-12 text-center">
            Our <span className="text-cyber-pink">Development</span> Process
          </h2>
          
          <div className="relative">
            {/* Process line */}
            <div className="hidden md:block absolute left-0 right-0 top-1/2 h-1 bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-neon transform -translate-y-1/2"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { number: "01", title: "Discovery", desc: "We learn about your business and project requirements." },
                { number: "02", title: "Design", desc: "We create wireframes and visual designs for your approval." },
                { number: "03", title: "Development", desc: "We build your website with clean, efficient code." },
                { number: "04", title: "Deployment", desc: "We launch your site and provide ongoing support." }
              ].map((step, index) => (
                <div key={index} className="relative">
                  <div className="cyber-card p-6 text-center h-full relative z-10">
                    <div className="w-12 h-12 mx-auto mb-4 bg-cyber-dark border-2 border-cyber-purple rounded-full flex items-center justify-center font-orbitron text-cyber-neon">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-orbitron mb-3 text-white">{step.title}</h3>
                    <p className="text-gray-300">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="cyber-card p-8 md:p-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-orbitron font-bold mb-6">
            Ready to <span className="text-cyber-pink">Transform</span> Your Online Presence?
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto mb-8">
            Let's discuss your project and create a website that perfectly matches your vision and business goals.
            Our team is ready to bring your ideas to life with cutting-edge web technology.
          </p>
          <Link to="/contact" className="cyber-button">
            <span>Get Started Today</span>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Services; 